import { useEffect, useState, useRef } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { getRole, logout } from '../../utils/auth';
import { BatchProvider } from '../../context/BatchProvider';
import { SelectionContext } from '../../context/SelectionContext';
import mbLogo from '../../assets/milwaukee-ballet-logo.png';
import agmaMark from '../../assets/agma-mark.png';

const adminNav = [
    { to: '/admin/photos',    label: 'Photos' },
    { to: '/admin/audit-log', label: 'Audit Log' },
    { to: '/admin/users',     label: 'Users' },
];

const adminDrawerNav = [
    { to: '/admin/photos',    label: 'Photos' },
    { to: '/admin/audit-log', label: 'Audit Log' },
    { to: '/admin/users',     label: 'Users' },
];

const artistNav = [
    { to: '/portfolio', label: 'My Portfolio' },
];

function HamburgerIcon() {
    return (
        <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
            <line x1='3' y1='6' x2='21' y2='6' />
            <line x1='3' y1='12' x2='21' y2='12' />
            <line x1='3' y1='18' x2='21' y2='18' />
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
            <line x1='18' y1='6' x2='6' y2='18' />
            <line x1='6' y1='6' x2='18' y2='18' />
        </svg>
    );
}

export default function AppShell() {
    const role = getRole();
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [adSelectedIds, setAdSelectedIds] = useState(new Set());
    const [shellError, setShellError] = useState('');
    const headerRef = useRef(null);

    const nav = role === 'admin' ? adminNav
        : role === 'artist' ? artistNav
        : [];

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    useEffect(() => {
        if (!drawerOpen) return;
        document.body.style.overflow = 'hidden';
        const handleKeyDown = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [drawerOpen]);

    useEffect(() => {
        const header = headerRef.current;
        if (!header) return;
        const setHeaderHeight = () => {
            document.documentElement.style.setProperty('--app-header-height', `${header.offsetHeight}px`);
        };
        setHeaderHeight();
        const observer = new ResizeObserver(setHeaderHeight);
        observer.observe(header);
        return () => observer.disconnect();
    }, []);

    return (
        <SelectionContext.Provider value={{ adSelectedIds, setAdSelectedIds }}>
        <div className='app-shell'>
            <header className='app-header' ref={headerRef}>
                <div className='app-header-brand'>
                    <img src={mbLogo} alt='Milwaukee Ballet' className='app-logo' />
                    <h1 className='app-title'>PHOTO APPROVAL</h1>
                    {role && (
                        <span className='app-role'>
                            {role === 'admin' ? 'Admin'
                                : role === 'artistic_director' ? 'Artistic Director'
                                : 'Artist'}
                        </span>
                    )}
                </div>

                <nav className='app-nav'>
                    {nav.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            className={({ isActive }) => `app-nav-link${isActive ? ' active' : ''}`}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className='app-header-account'>
                    <Link to='/change-password' className='app-nav-btn' style={{ textDecoration: 'none' }}>My Account</Link>
                    <button onClick={handleLogout} className='app-nav-btn'>Log out</button>
                </div>

                {role === 'admin' && (
                    <button
                        type='button'
                        className='app-nav-hamburger'
                        aria-label='Open menu'
                        aria-expanded={drawerOpen}
                        onClick={() => setDrawerOpen(true)}
                    >
                        <HamburgerIcon />
                    </button>
                )}
            </header>

            {shellError && (
                <div className='shell-error-banner'>
                    {shellError}
                    <button className='inline-error-dismiss' onClick={() => setShellError('')}>×</button>
                </div>
            )}

            {role === 'admin' && (
                <>
                    <div
                        className={`app-drawer-overlay${drawerOpen ? ' open' : ''}`}
                        onClick={() => setDrawerOpen(false)}
                    />
                    <div
                        className={`app-drawer${drawerOpen ? ' open' : ''}`}
                        role='dialog'
                        aria-modal='true'
                        aria-label='Menu'
                    >
                        <button
                            type='button'
                            className='app-drawer-close'
                            aria-label='Close menu'
                            onClick={() => setDrawerOpen(false)}
                        >
                            <CloseIcon />
                        </button>
                        <nav className='app-drawer-nav'>
                            {adminDrawerNav.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    className={({ isActive }) => `app-drawer-link${isActive ? ' active' : ''}`}
                                    onClick={() => setDrawerOpen(false)}
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                        </nav>
                        <div className='app-drawer-account'>
                            <Link to='/change-password' className='app-drawer-link' onClick={() => setDrawerOpen(false)}>
                                My Account
                            </Link>
                            <button
                                type='button'
                                onClick={() => { setDrawerOpen(false); handleLogout(); }}
                                className='app-drawer-link app-drawer-logout'
                            >
                                Log out
                            </button>
                        </div>
                    </div>
                </>
            )}

            <main className='app-main'>
                <BatchProvider>
                    <Outlet />
                </BatchProvider>
            </main>

            <footer className='app-footer'>
                <img src={agmaMark} alt='AGMA' className='app-footer-mark' />
                <span className='app-footer-text'>
                    This system implements the photo approval rights guaranteed under
                    AGMA Collective Bargaining Agreement, Section 4, Article 41.
                </span>
            </footer>
        </div>
        </SelectionContext.Provider>
    );
}